/**
 * ticketRegressionCart.ts — Carrito de regresión para validar desglose del ticket.
 *
 * Replica la venta multitenant demo (16 líneas, 36 categorías) sin clics manuales.
 * Conteo esperado: 16 ítems · 23 uds · 3 kg · $34.517.500
 */
import type { CartItem, Producto, VariantGroup } from "@/types";
import type { Venta } from "@/features/caja/types/caja.types";
import { mapLineToVentaProducto } from "@/features/caja/utils/ventaProductoMapper";
import { subtotalLineaVentaProducto } from "@/features/caja/utils/ventaProductosLineas";
import {
  buildVariantKey,
  findSkuByKey,
} from "@/components/ui/variants/variantHelpers";
import {
  buildModifierKey,
  buildModifierLabels,
  sumModifierExtras,
  type ModifierSelection,
} from "@/components/ui/modifiers/modifierHelpers";
import type { VarianteSeleccionada } from "@/lib/formatLineSelections";
import { getDemoProductsForPackIds } from "@/mocks/data/products/demoCatalog";
import { cat_desay_products } from "@/mocks/data/products/desayuno";
import { cat_extra_products } from "@/mocks/data/products/extras";
import { isSameLocalDate } from "@/lib/utils/formatters";

export const TICKET_DEMO_VENTA_ID = "altenot-demo-ticket-regression";

export const TICKET_REGRESSION_EXPECTED = {
  itemCount: 16,
  unitQty: 23,
  kgQty: 3,
  totalCop: 34_517_500,
  compactHint: "16 ítems · 23 uds · 3 kg",
} as const;

export const isTicketDemoVenta = (id: string): boolean =>
  id === TICKET_DEMO_VENTA_ID;

/** Packs demo requeridos para la venta de regresión (16 líneas). */
export const REGRESSION_REQUIRED_PACK_IDS = ["cat_desay", "cat_extra"] as const;

const findProductByName = (
  products: Producto[],
  nombre: string,
): Producto | undefined => products.find((p) => p.nombre === nombre);

/** Snapshots estáticos por si el inventario local no tiene Desayuno/Extras. */
const REGRESSION_FALLBACK_PRODUCTS: Producto[] = [
  findProductByName(cat_desay_products as Producto[], "Desayuno Completo"),
  findProductByName(cat_extra_products as Producto[], "Adiciones al Pedido"),
].filter((p): p is Producto => Boolean(p));

/** Une catálogo vivo + packs demo obligatorios + fallbacks embebidos. */
export const mergeRegressionCatalog = (products: Producto[]): Producto[] => {
  const required = getDemoProductsForPackIds([
    ...REGRESSION_REQUIRED_PACK_IDS,
  ]) as Producto[];
  const byId = new Map<string, Producto>();
  for (const p of [...required, ...REGRESSION_FALLBACK_PRODUCTS, ...products]) {
    byId.set(p.id, p);
  }
  return [...byId.values()];
};

type RegressionLineSpec = {
  nombre: string;
  quantity?: number;
  peso?: number;
  variantSelection?: Record<string, string>;
  modifierSelection?: ModifierSelection;
};

const REGRESSION_LINES: RegressionLineSpec[] = [
  {
    nombre: "Gafas de Sol Unisex",
    variantSelection: { grp_color: "op_bei" },
  },
  { nombre: "Papel Higiénico Familia x12" },
  { nombre: "Limpiaparabrisas Bosch" },
  {
    nombre: "Jugo Hit Naranja 200ml",
    variantSelection: { grp_pres: "op_500" },
  },
  {
    nombre: "Paleta Sombras Urban Decay",
    variantSelection: { grp_tono: "op_nude" },
  },
  {
    nombre: "Zapato Formal Hombre Cuero",
    variantSelection: { grp_talla: "op_t35", grp_color: "op_neg" },
  },
  { nombre: "Salchicha Ranchera Zenú x8", peso: 3 },
  { nombre: "Perro Mixto Especial" },
  { nombre: "Cuerda de Saltar" },
  {
    nombre: "Desayuno Completo",
    modifierSelection: {
      des_dc_caldo: ["des_dc_k3"],
      des_dc_huevos: ["des_dc_h2"],
      des_dc_arroz_pan: ["des_dc_ap1"],
      des_dc_bebida: ["des_dc_b2"],
      des_dc_cubiertos: ["des_dc_c1"],
    },
  },
  {
    nombre: "Adiciones al Pedido",
    modifierSelection: {
      ext_ad_extras: [
        "ext_ad_icopor",
        "ext_ad_icopor",
        "ext_ad_icopor",
        "ext_ad_icopor",
        "ext_ad_icopor",
        "ext_ad_agua",
        "ext_ad_agua",
        "ext_ad_agua",
      ],
    },
  },
  {
    nombre: "Anillo Compromiso Diamante",
    variantSelection: { grp_mat: "op_oro", grp_talla: "op_7" },
  },
  {
    nombre: "Leggings Deportivos Mujer",
    variantSelection: { grp_talla: "op_xxl", grp_color: "op_bei" },
  },
  {
    nombre: "Laptop Lenovo ThinkPad",
    quantity: 2,
    variantSelection: { grp_cap: "op_256", grp_color: "op_pt" },
  },
  {
    nombre: "Laptop Lenovo ThinkPad",
    variantSelection: { grp_cap: "op_128", grp_color: "op_mn" },
  },
  {
    nombre: "Laptop Lenovo ThinkPad",
    variantSelection: { grp_cap: "op_512", grp_color: "op_gr" },
  },
];

const variantLabelsFromSelection = (
  grupos: VariantGroup[],
  seleccion: Record<string, string>,
): VarianteSeleccionada[] => {
  const out: VarianteSeleccionada[] = [];
  for (const g of grupos) {
    const optId = seleccion[g.id];
    if (!optId) continue;
    const opt = g.opciones.find((o) => o.id === optId);
    if (!opt) continue;
    out.push({
      groupId: g.id,
      groupLabel: g.label,
      optionId: opt.id,
      optionLabel: opt.label,
      ...(opt.precioExtra != null && opt.precioExtra > 0
        ? { precioExtra: opt.precioExtra }
        : {}),
    });
  }
  return out;
};

const resolveVariantPricing = (
  product: Producto,
  seleccion: Record<string, string>,
) => {
  const grupos = product.variantes ?? [];
  let precioExtra = 0;
  for (const g of grupos) {
    const optId = seleccion[g.id];
    const opt = g.opciones.find((o) => o.id === optId);
    if (opt) precioExtra += opt.precioExtra ?? 0;
  }
  const variantKey = buildVariantKey(seleccion);
  const skuActivo = findSkuByKey(product, variantKey);
  const precioFinal =
    skuActivo?.precioOverride != null
      ? skuActivo.precioOverride
      : product.precio + precioExtra;
  const stockDisponible = skuActivo?.stock ?? product.stock;
  return {
    variantKey,
    skuActivo,
    precioFinal: Math.round(precioFinal),
    stockDisponible,
    labels: variantLabelsFromSelection(grupos, seleccion),
  };
};

const buildRegressionCartItem = (
  product: Producto,
  spec: RegressionLineSpec,
): CartItem => {
  const qty = spec.quantity ?? 1;

  if (spec.peso != null && spec.peso > 0) {
    const ppk = product.precioPorKilo ?? product.precio;
    return {
      ...product,
      quantity: qty,
      peso: spec.peso,
      precio: Math.round(spec.peso * ppk),
    };
  }

  let precio = product.precio;
  let variantKey: string | undefined;
  let variantesSeleccionadas: VarianteSeleccionada[] | undefined;
  let skuVariante: string | undefined;
  let stockVariante: number | undefined;

  if (spec.variantSelection && Object.keys(spec.variantSelection).length > 0) {
    const resolved = resolveVariantPricing(product, spec.variantSelection);
    variantKey = resolved.variantKey;
    variantesSeleccionadas = resolved.labels;
    skuVariante = resolved.skuActivo?.sku;
    stockVariante = resolved.stockDisponible;
    precio = resolved.precioFinal;
  }

  let modifierKey: string | undefined;
  let modificadoresSeleccionados:
    | CartItem["modificadoresSeleccionados"]
    | undefined;

  if (
    spec.modifierSelection &&
    Object.keys(spec.modifierSelection).length > 0
  ) {
    const grupos = product.gruposModificadores ?? [];
    modificadoresSeleccionados = buildModifierLabels(
      grupos,
      spec.modifierSelection,
    );
    modifierKey = buildModifierKey(spec.modifierSelection);
    precio += sumModifierExtras(grupos, spec.modifierSelection);
  }

  return {
    ...product,
    quantity: qty,
    precio: Math.round(precio),
    ...(variantKey
      ? {
          variantKey,
          variantesSeleccionadas,
          skuVariante,
          stockVariante,
        }
      : {}),
    ...(modifierKey ? { modifierKey, modificadoresSeleccionados } : {}),
  };
};

export interface TicketRegressionCartResult {
  items: CartItem[];
  missing: string[];
}

/** Construye el carrito de regresión a partir del inventario cargado en Caja. */
export const buildTicketRegressionCart = (
  products: Producto[],
): TicketRegressionCartResult => {
  const merged = mergeRegressionCatalog(products);
  const byName = new Map(merged.map((p) => [p.nombre, p]));
  const items: CartItem[] = [];
  const missing: string[] = [];

  for (const spec of REGRESSION_LINES) {
    const product = byName.get(spec.nombre);
    if (!product) {
      missing.push(spec.nombre);
      continue;
    }
    items.push(buildRegressionCartItem(product, spec));
  }

  return { items, missing };
};

export interface TicketRegressionVentaMeta {
  usuarioId?: string;
  userName?: string;
  /** Empleado que abrió la sesión de caja (distinto del vendedor del ticket). */
  sesionAbiertaPorNombre?: string;
  /** ISO datetime; por defecto hoy ~ mediodía local. */
  fecha?: string;
  puntoCajaId?: string;
  puntoCajaNombre?: string;
}

/** Venta sintética para Historial — misma referencia que el botón Demo ticket en Caja. */
export const buildTicketRegressionVenta = (
  products: Producto[],
  meta: TicketRegressionVentaMeta = {},
): (Venta & { numeroOrden: number }) | null => {
  const { items, missing } = buildTicketRegressionCart(products);
  if (items.length === 0) return null;

  const productos = items.map((item) =>
    mapLineToVentaProducto(item, item.nombre, item.precio),
  );
  const total = productos.reduce(
    (sum, p) => sum + subtotalLineaVentaProducto(p),
    0,
  );

  const now = new Date();
  const demoDate =
    meta.fecha ??
    new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      12,
      57,
      0,
    ).toISOString();

  return {
    id: TICKET_DEMO_VENTA_ID,
    fecha: demoDate,
    total,
    subtotal: total,
    metodoPago: "efectivo",
    metodoPagoLabel: "Efectivo",
    detalleMetodoPago: {
      breakdown: [
        {
          method: "efectivo",
          methodLabel: "Efectivo",
          amount: total,
          received: total,
        },
      ],
    },
    productos,
    usuarioId: meta.usuarioId ?? "demo-user",
    userName: meta.userName ?? "Juan Pérez",
    sesionAbiertaPorId: "demo-session-opener",
    sesionAbiertaPorNombre: meta.sesionAbiertaPorNombre ?? "María López",
    recibido: total,
    cambio: 0,
    estado: "activa",
    numeroOrden: 1,
    puntoCajaId: meta.puntoCajaId ?? "pc1",
    puntoCajaNombre: meta.puntoCajaNombre ?? "Caja principal",
    nota:
      missing.length > 0
        ? `Referencia demo (${missing.length} producto(s) faltante(s) en catálogo).`
        : "Venta de referencia para validar desglose del ticket (no se guardó en caja).",
  };
};

/** ¿La venta demo coincide con búsqueda/filtros del historial? */
export const ticketDemoMatchesHistorialFilters = (
  venta: Venta,
  opts: {
    searchTerm: string;
    filterMetodoPago: string;
    filterHora: string;
    filterOperacion:
      | ""
      | "con_descuento"
      | "con_propina"
      | "anuladas"
      | "con_devolucion"
      | "creditos";
    filterEmpleadoId: string;
    filterPuntoCajaId: string;
    fechaSeleccionada: string;
    filterVentaId?: string;
  },
): boolean => {
  if (opts.filterVentaId) return false;
  if (
    opts.filterHora &&
    new Date(venta.fecha).getHours() !== Number(opts.filterHora)
  ) {
    return false;
  }
  if (opts.filterOperacion) return false;
  if (
    opts.filterEmpleadoId &&
    (venta.usuarioId || "unknown") !== opts.filterEmpleadoId
  ) {
    return false;
  }
  if (opts.filterPuntoCajaId) return false;
  if (opts.filterMetodoPago && opts.filterMetodoPago !== "efectivo") {
    return false;
  }

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const fechaFiltro = opts.fechaSeleccionada || todayIso;
  if (!isSameLocalDate(venta.fecha, fechaFiltro)) {
    return false;
  }

  const q = opts.searchTerm.trim().toLowerCase();
  if (!q) return true;

  return (
    venta.id.toLowerCase().includes(q) ||
    venta.total.toString().includes(opts.searchTerm.trim()) ||
    (venta.userName?.toLowerCase().includes(q) ?? false) ||
    q.includes("demo") ||
    venta.productos.some((p) => p.nombre.toLowerCase().includes(q))
  );
};
